import { useTranslation } from 'react-i18next';
import { PLATFORM_LANGUAGES, DEFAULT_NAME_FIELD_REGEX } from '@thinknet-gmbh/platform-common';
import {
  Button,
  Form,
  TextInput,
  Typography,
  TYPOGRAPHY_SIZE,
  SelectInput,
  PasswordInput,
  ButtonsWrapper,
  PhoneNumberInput,
  AlertBlock,
} from '@thinknet-gmbh/platform-common-ui';

import { useUpdateCurrentProfile } from 'src/hooks/profiles/update-current-profile.hook';
import { useSendChangeEmailLink } from 'src/hooks/accounts/send-change-email-link.hook';

import { IProfileEdit } from './profile-edit.interfaces';
import { EditContainer, GridElement } from './profile-edit.styles';

const ProfileEdit = ({ cancelEdit, data }: IProfileEdit) => {
  const [form] = Form.useForm();
  const { t } = useTranslation(['common', 'settings']);

  const updateCurrentProfile = useUpdateCurrentProfile();
  const sendChangeEmailLink = useSendChangeEmailLink();

  const onFinish = (values: any) => {
    updateCurrentProfile({
      variables: {
        payload: values.profile,
      },
    });

    if (values.password) {
      sendChangeEmailLink({
        variables: {
          payload: {
            currentPassword: values.password,
            email: values.account.email,
          },
        },
      });
    }

    cancelEdit(false);
  };

  return (
    <EditContainer>
      <Typography size={TYPOGRAPHY_SIZE.medium} weight={'bold'}>
        {t('common:labels.userProfile')}
      </Typography>
      <Form layout="vertical" form={form} onFinish={onFinish} initialValues={data}>
        <GridElement>
          <Form.Item
            label={t('common:labels.firstName')}
            name={['profile', 'firstName']}
            rules={[
              {
                required: true,
                message: t('common:validation.error.firstNameRequired'),
              },
              {
                pattern: DEFAULT_NAME_FIELD_REGEX,
                message: t('common:validation.error.incorrectFormat'),
              },
            ]}
          >
            <TextInput maxLength={50} />
          </Form.Item>
          <Form.Item
            label={t('common:labels.lastName')}
            name={['profile', 'lastName']}
            rules={[
              {
                required: true,
                message: t('common:validation.error.lastNameRequired'),
              },
              {
                pattern: DEFAULT_NAME_FIELD_REGEX,
                message: t('common:validation.error.incorrectFormat'),
              },
            ]}
          >
            <TextInput maxLength={50} />
          </Form.Item>
          <Form.Item
            label={t('common:labels.phoneNumber')}
            name={['profile', 'phoneNumber']}
            rules={[
              {
                required: true,
                message: t('common:validation.error.phoneNumberRequired'),
              },
              () => ({
                validator(_, value) {
                  if (String(value).length >= 12 && String(value).length <= 15) {
                    return Promise.resolve();
                  }

                  return Promise.reject(new Error(t('common:validation.error.incorrectFormat')));
                },
              }),
            ]}
          >
            <PhoneNumberInput />
          </Form.Item>
        </GridElement>
        <GridElement>
          <Form.Item
            label={t('settings:userManagementSettings.labels.position')}
            name={['companyPosition', 'name']}
          >
            <TextInput disabled placeholder="-" />
          </Form.Item>
          <Form.Item
            label={t('common:labels.permission')}
            name={['account', 'permissionGroup']}
            rules={[{ required: true }]}
          >
            <TextInput
              disabled
              placeholder={t('settings:permissionGroupSettings.placeholders.choosePermissionGroup')}
            />
          </Form.Item>
          <Form.Item
            required
            label={t('settings:userProfileSettings.labels.chooseYourLanguage')}
            name={['profile', 'language']}
          >
            <SelectInput>
              <SelectInput.Option value={PLATFORM_LANGUAGES.de}>
                {t('common:languages.de')}
              </SelectInput.Option>
              <SelectInput.Option value={PLATFORM_LANGUAGES.en}>
                {t('common:languages.en')}
              </SelectInput.Option>
            </SelectInput>
          </Form.Item>
        </GridElement>
        <GridElement>
          <Form.Item
            label={t('common:labels.email')}
            name={['account', 'email']}
            rules={[
              { required: true, message: t('common:validation.error.emailRequired') },
              { type: 'email', message: t('common:validation.error.incorrectFormat') },
            ]}
          >
            <TextInput maxLength={50} />
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currValues) =>
              prevValues.account.email !== currValues.account.email
            }
          >
            {({ getFieldValue }) => {
              const emailIsChanged = getFieldValue(['account', 'email']) !== data.account.email;

              return (
                <>
                  <Form.Item
                    hidden={!emailIsChanged || !data.account.hasPassword}
                    rules={[{ required: emailIsChanged }]}
                    name="password"
                    label={t('settings:userProfileSettings.labels.passwordIfChangingEmail')}
                  >
                    <PasswordInput
                      placeholder={t('settings:userProfileSettings.placeholders.enterYourPassword')}
                    />
                  </Form.Item>
                  {emailIsChanged && !data.account.hasPassword && (
                    <AlertBlock title={t('common:labels.warning')}>
                      {t('common:validation.error.emailChangeRequirePassword')}
                    </AlertBlock>
                  )}
                </>
              );
            }}
          </Form.Item>
        </GridElement>
        <ButtonsWrapper>
          <Button type="primary" htmlType="submit">
            {t('common:buttons.save')}
          </Button>
          <Button onClick={() => cancelEdit(false)}>{t('common:buttons.cancel')}</Button>
        </ButtonsWrapper>
      </Form>
    </EditContainer>
  );
};

export default ProfileEdit;
